import React, { useContext, useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { Collapse, Input, Label } from 'reactstrap';
import styles from './MultipleChoiceQuestion.module.scss';
import { AnswersContext } from '../Survey';
import { IconChevronDown } from '@tabler/icons-react';

export default function MultipleChoiceQuestion({ _id, data }) {
    const { submitAnswer, answers, isMobile } = useContext(AnswersContext);
    const value = useMemo(() => answers[_id] || [], [answers, _id]);
    const [isOpen, setIsOpen] = useState(false);
    const [visibleCount, setVisibleCount] = useState(value.length);
    const [width, setWidth] = useState(0);
    const containerRef = useRef(null);
    const selectedValueRef = useRef(null);
    const measureRef = useRef(null);

    const toggle = () => setIsOpen(!isOpen);

    useLayoutEffect(() => {
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setWidth(entry.contentRect.width);
            }
        });
        if (selectedValueRef.current) {
            observer.observe(selectedValueRef.current);
        }
        return () => observer.disconnect();
    }, []);

    useLayoutEffect(() => {
        if (value.length === 0 || !selectedValueRef.current || !measureRef.current) {
            setVisibleCount(value.length);
            return;
        }

        const containerWidth = selectedValueRef.current.offsetWidth;
        const items = value.map((index) => data[index]);

        // Use the hidden span to measure text widths
        const measure = measureRef.current;

        let currentText = '';

        for (let i = 0; i < items.length; i++) {
            const nextItem = items[i];
            const testText = currentText + (currentText ? ', ' : '') + nextItem;

            measure.textContent = testText;
            const testWidth = measure.offsetWidth;

            if (testWidth <= containerWidth) {
                currentText = testText;
            } else {
                // Doesn't fit, need to show "... und X weitere Antworten"
                // Try to fit as many as possible with the indicator
                for (let j = i; j >= 0; j--) {
                    const remaining = items.length - j;
                    const indicator =
                        remaining === 1 ? `, ... und eine weitere Antwort` : `, ... und ${remaining} weitere Antworten`;
                    const baseItems = items.slice(0, j);
                    const baseText = baseItems.join(', ');
                    const combinedText =
                        baseText + (baseText ? indicator : remaining === 1 ? `Eine Antwort` : `${remaining} Antworten`);

                    measure.textContent = combinedText;
                    if (measure.offsetWidth <= containerWidth || j === 0) {
                        setVisibleCount(j);
                        return;
                    }
                }
                setVisibleCount(0);
                return;
            }
        }
        setVisibleCount(items.length);
    }, [value, data, width]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen]);

    const handleBlur = (e) => {
        if (isOpen && e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
            setIsOpen(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
        }
    };

    const handleChange = (index) => {
        let newValue;
        if (value.includes(index)) {
            newValue = value.filter((i) => i !== index);
        } else {
            newValue = [...value, index];
        }
        newValue.sort((a, b) => a - b);
        submitAnswer(_id, newValue);
    };

    if (isMobile) {
        return (
            <div id={_id} className={styles.MultipleChoiceQuestion}>
                {data.map((item, index) => (
                    <div key={index} className={styles.CheckboxAnswer} onClick={() => handleChange(index)}>
                        <Input type="checkbox" id={_id + index} name={_id} checked={value.includes(index)} readOnly />
                        <Label for={_id + index} onClick={(e) => e.preventDefault()}>
                            {item}
                        </Label>
                    </div>
                ))}
            </div>
        );
    }

    const fullSelectedText =
        value.length > 0 ? value.map((index) => data[index]).join(', ') : 'Bitte treffen Sie eine Wahl';

    let textPart = '';
    let indicatorPart = '';
    if (value.length === 0) {
        textPart = 'Bitte treffen Sie eine Wahl';
    } else if (visibleCount === value.length) {
        textPart = fullSelectedText;
    } else if (visibleCount === 0 && value.length > 0) {
        textPart = data[value[0]];
        const remaining = value.length - 1;
        if (remaining > 0) {
            indicatorPart = remaining === 1 ? ' und eine weitere Antwort' : ` und ${remaining} weitere Antworten`;
        }
    } else {
        const remaining = value.length - visibleCount;
        textPart =
            value
                .slice(0, visibleCount)
                .map((index) => data[index])
                .join(', ') + ', ...';
        indicatorPart = remaining === 1 ? ' und eine weitere Antwort' : ` und ${remaining} weitere Antworten`;
    }

    return (
        <div>
            <div className={styles.MultipleChoiceQuestion}>
                <div
                    ref={containerRef}
                    className={`${styles.SelectContainer} ${isOpen ? styles.isOpen : ''}`}
                    onBlur={handleBlur}
                >
                    <div
                        id={_id}
                        className={`${styles.SelectToggle} ${isOpen ? styles.isOpen : ''}`}
                        onClick={toggle}
                        onKeyDown={handleKeyDown}
                        role="button"
                        tabIndex="0"
                        aria-expanded={isOpen}
                    >
                        <div className={styles.ToggleContent}>
                            <Input type="checkbox" checked={value.length > 0} disabled aria-hidden="true" />
                            <span className="visually-hidden">
                                Aktuell gewählte Optionen: {fullSelectedText}. Drücken Sie hier, um die Optionen
                                auszuwählen.
                            </span>
                            <div className={styles.SelectedValue} aria-hidden="true" ref={selectedValueRef}>
                                <span className={styles.TextPart}>{textPart}</span>
                                {indicatorPart && <span className={styles.IndicatorPart}>{indicatorPart}</span>}
                            </div>
                            <span ref={measureRef} className={styles.MeasureSpan} aria-hidden="true" />
                        </div>
                        <IconChevronDown size={20} className={`${styles.Chevron} ${isOpen ? styles.rotated : ''}`} />
                    </div>
                    <Collapse isOpen={isOpen} className={styles.CollapseWrapper}>
                        <div className={styles.OptionsContainer}>
                            {data.map((item, index) => (
                                <div key={index} className={styles.CheckboxAnswer} onClick={() => handleChange(index)}>
                                    <Input
                                        type="checkbox"
                                        id={_id + index}
                                        name={_id}
                                        checked={value.includes(index)}
                                        readOnly
                                    />
                                    <Label for={_id + index} onClick={(e) => e.preventDefault()}>
                                        {item}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    </Collapse>
                </div>
            </div>
        </div>
    );
}
